import React, { useState, useCallback, useEffect } from "react";
import { Chat, type ChatMessage, type ChatMessageAttachment } from "@/components/chat/Chat";
import { useWorkerCreation } from "@/context/WorkerCreationContext";
import type { SystemTask } from "@/components/tasks/SystemTasks";
import WorkerAvatar from "@/components/worker/WorkerAvatar";
import { WorkerAniAPI } from "@/services/api";
import { WS_CHANNELS } from "@/utils/websocket";
import { useAuth } from "@/context/AuthContext";

export default function CreateWorkerTestWorker() {
  const { workerData } = useWorkerCreation();
  const { user } = useAuth();
  const [conversationHistory, setConversationHistory] = useState<ChatMessage[]>(
    []
  );
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize conversation when component mounts
  useEffect(() => {
    const initializeConversation = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Check if we have a real worker ID (draft or deployed)
        if (!workerData._id) {
          setError(
            "No worker ID available. Please complete the worker setup first."
          );
          setIsLoading(false);
          return;
        }

        // Create a new conversation for testing with the real worker
        const conversation = await WorkerAniAPI.createWorkerConversation({
          worker_card_id: workerData._id,
          title: `Test Chat with ${workerData.name}`,
        });

        setConversationId(conversation.id || conversation._id);
      } catch (error) {
        console.error("Failed to initialize conversation:", error);
        setError("Failed to initialize test conversation. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    // Only initialize if we have the worker name and ID
    if (workerData.name && workerData._id) {
      initializeConversation();
    }
  }, [workerData._id, workerData.name]);

  // Create initial assistant message based on worker data
  const initialAssistantMessage = `👋 Hi! I'm ${workerData.name}. ${workerData.bio} How can I help?`;

  // Handle sending messages
  const handleSendMessage = useCallback(
    async (
      recipientId: string,
      data: {
        content: string;
        role: string;
        attachments?: ChatMessageAttachment[];
      },
    ): Promise<ChatMessage> => {
      console.log("[handleSendMessage] called", {
        recipientId,
        data,
        conversationId,
      });
      if (!conversationId) {
        console.error("[handleSendMessage] No conversationId available");
        throw new Error("Conversation not initialized");
      }

      // Optimistically add the user message to local state
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        content: data.content,
        sender: {
          id: user?.id || "current-user",
          name: user?.fullname || "You",
          image: user?.image,
          type: "user",
        },
        timestamp: Date.now(),
        attachments: data.attachments || [],
      };
      setConversationHistory((prev) => [...prev, userMessage]);
      console.log(
        "[handleSendMessage] Optimistically added user message",
        userMessage
      );

      try {
        console.log("[handleSendMessage] Sending API request", {
          conversationId,
          payload: {
            role: "user",
            content: data.content,
            attachments: data.attachments || [],
            conversation_id: conversationId,
          },
          workerId: workerData._id,
        });

        // Send message to the worker conversation API using the real worker
        const response = await WorkerAniAPI.sendWorkerMessage(
          conversationId,
          {
            role: "user",
            content: data.content,
            attachments: data.attachments || [],
            conversation_id: conversationId,
          },
          false // isTest = false, use real worker
        );
        console.log("[handleSendMessage] API response", response);

        // Only add the assistant message if it's not a user message
        if (response.role !== "user") {
          const assistantMessage: ChatMessage = {
            id: response._id,
            content: response.content,
            sender: {
              id: "worker",
              name: workerData.name,
              type: "assistant",
            },
            timestamp: new Date(response.timestamp).getTime(),
            attachments: response.attachments,
          };

          // Add assistant message to local state
          setConversationHistory((prev) => [...prev, assistantMessage]);
          console.log(
            "[handleSendMessage] Added assistant message",
            assistantMessage
          );

          return assistantMessage;
        }
        // If the backend echoes the user message, just return the optimistically added userMessage
        return userMessage;
      } catch (error) {
        console.error("[handleSendMessage] Error sending message", error);
        throw error;
      }
    },
    [conversationId, workerData._id, workerData.name, user?.fullname, user?.id, user?.image],
  );

  // Fetch chat history
  const handleFetchChatHistory = useCallback(
    async (_recipientId: string): Promise<ChatMessage[]> => {
      // For CreateWorkerTest, we manage history locally and don't re-fetch from server
      // This prevents messages from disappearing during the test session

      // Only add initial message if there's no conversation history at all
      if (conversationHistory.length === 0) {
        const initialMessage: ChatMessage = {
          id: "initial-assistant-message",
          content: initialAssistantMessage,
          sender: {
            id: "ani",
            name: workerData.name,
            type: "assistant",
          },
          timestamp: Date.now() - 1000, // Slightly in the past
        };

        return [initialMessage];
      }

      // Return the current conversation history as-is to prevent clearing
      return conversationHistory;
    },
    [conversationHistory, initialAssistantMessage, workerData.name]
  );

  // Fetch system tasks (empty for now)
  const handleFetchSystemTasks = useCallback(
    async (_recipientId: string): Promise<SystemTask[]> => {
      return [];
    },
    []
  );

  // Custom avatar component for the worker
  const workerAvatarComponent = (
    <WorkerAvatar seed={workerData.name} size={32} />
  );

  if (isLoading || !conversationId) {
    return (
      <div className="h-full flex flex-col">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200">
            Test Your Worker
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {!workerData._id
              ? "Please complete the worker setup to test your worker..."
              : `Initializing conversation with ${workerData.name}...`}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex flex-col">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200">
            Test Your Worker
          </h2>
          <p className="text-red-500 dark:text-red-400 mt-1">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200">
          Test Your Worker
        </h2>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          Interact with {workerData.name} in a live chat environment to test
          their capabilities. This is the actual worker that will be deployed.
        </p>
      </div>

      <div className="flex-1 min-h-0">
        <Chat
          recipientId="test-worker"
          recipientName={workerData.name}
          recipientAvatar={workerData.name}
          conversationId={conversationId}
          sendMessage={handleSendMessage}
          fetchHistory={handleFetchChatHistory}
          fetchSystemTasks={handleFetchSystemTasks}
          chatChannel={WS_CHANNELS.WORKER_CHAT}
          systemTaskChannel={WS_CHANNELS.SYSTEM_TASKS}
          enableSystemJobs={false}
          useDirectResponse={false}
          initialAssistantMessage={initialAssistantMessage}
          isContextActive={true}
          customAvatar={workerAvatarComponent}
        />
      </div>
    </div>
  );
}
